// Risk calculation utilities for the Global Mining Risk application

// Risk level thresholds based on Fragile States Index scoring
export const RISK_THRESHOLDS = {
  'very-low': { min: 0, max: 29.9, label: 'Very Low Risk', color: '#388e3c' },
  'low': { min: 30, max: 49.9, label: 'Low Risk', color: '#689f38' },
  'medium': { min: 50, max: 69.9, label: 'Medium Risk', color: '#fbc02d' },
  'high': { min: 70, max: 89.9, label: 'High Risk', color: '#f57c00' },
  'very-high': { min: 90, max: Infinity, label: 'Very High Risk', color: '#d32f2f' }
};

/**
 * Calculate the risk category based on a numerical score
 * @param {number} score - Risk score (0-120+)
 * @returns {string} Risk category ('very-low', 'low', 'medium', 'high', 'very-high')
 */
export function getRiskCategory(score) {
  if (typeof score !== 'number' || isNaN(score)) {
    return 'unknown';
  }

  for (const [category, threshold] of Object.entries(RISK_THRESHOLDS)) {
    if (score >= threshold.min && score <= threshold.max) {
      return category;
    }
  }

  // Fallback for scores above the highest threshold
  return 'very-high';
}

/**
 * Get the risk color for a given score
 * @param {number} score - Risk score
 * @returns {string} Hex color code
 */
export function getRiskColor(score) {
  const category = getRiskCategory(score);
  return RISK_THRESHOLDS[category]?.color || '#666666';
}

/**
 * Get the human-readable label for a risk category
 * @param {string} category - Risk category
 * @returns {string} Human-readable label
 */
export function getRiskLabel(category) {
  return RISK_THRESHOLDS[category]?.label || 'Unknown Risk';
}

/**
 * Calculate composite risk score for a mine based on country risk and mine-specific factors
 * @param {Object} mineData - Mine data object
 * @param {Object} countryRisk - Country risk data object
 * @returns {number} Composite risk score
 */
export function calculateCompositeRisk(mineData, countryRisk) {
  if (!countryRisk || typeof countryRisk.score !== 'number') {
    return 50; // Default medium risk if no country data
  }

  // Start with base country risk score
  let adjustedRisk = countryRisk.score;

  // Adjust based on mine-specific factors
  if (mineData) {
    // Mine status adjustments
    if (mineData.status) {
      switch (mineData.status.toLowerCase()) {
        case 'abandoned':
        case 'closed':
          adjustedRisk += 15; // Abandoned mines have higher environmental/social risk
          break;
        case 'development':
        case 'construction':
          adjustedRisk += 8; // Development phase has regulatory risks
          break;
        case 'exploration':
          adjustedRisk += 5; // Early stage uncertainty
          break;
        case 'active':
        case 'producing':
          // No adjustment for active mines
          break;
        case 'suspended':
        case 'care and maintenance':
          adjustedRisk += 10; // Operational uncertainty
          break;
      }
    }

    // Mine type adjustments
    if (mineData.mine_type) {
      switch (mineData.mine_type.toLowerCase()) {
        case 'underground':
          adjustedRisk += 5; // Underground mining has higher safety risks
          break;
        case 'open pit':
        case 'surface':
          adjustedRisk += 3; // Surface mining has environmental impact
          break;
        case 'placer':
          adjustedRisk += 2; // Lower risk mining method
          break;
      }
    }

    // Commodity-specific risk adjustments
    if (mineData.primary_commodity) {
      const commodity = mineData.primary_commodity.toLowerCase();

      // High-value/strategic commodities may have higher geopolitical risk
      const strategicCommodities = [
        'lithium', 'cobalt', 'rare earths', 'uranium', 'tantalum',
        'niobium', 'tungsten', 'titanium', 'platinum', 'palladium'
      ];

      if (strategicCommodities.some(strategic => commodity.includes(strategic))) {
        adjustedRisk += 5; // Strategic commodities have geopolitical risk
      }

      // Environmental risk commodities
      const environmentalRiskCommodities = [
        'coal', 'uranium', 'asbestos', 'mercury'
      ];

      if (environmentalRiskCommodities.some(risky => commodity.includes(risky))) {
        adjustedRisk += 8; // Higher environmental/health risks
      }
    }

    // Size/scale adjustments (if data available)
    if (mineData.production_scale) {
      switch (mineData.production_scale.toLowerCase()) {
        case 'large':
        case 'major':
          adjustedRisk += 3; // Larger operations have more stakeholder risk
          break;
        case 'artisanal':
        case 'small':
          adjustedRisk += 7; // Small-scale may have less oversight
          break;
      }
    }
  }

  // Normalize to reasonable bounds (0-120)
  return Math.min(120, Math.max(0, adjustedRisk));
}

/**
 * Calculate mine-specific environmental risk score
 * @param {Object} mineData - Mine data object
 * @returns {number} Environmental risk score (0-100)
 */
export function calculateEnvironmentalRisk(mineData) {
  let envRisk = 30; // Base environmental risk

  if (!mineData) return envRisk;

  // Mine type impact
  if (mineData.mine_type) {
    switch (mineData.mine_type.toLowerCase()) {
      case 'open pit':
        envRisk += 20; // High surface disturbance
        break;
      case 'underground':
        envRisk += 10; // Subsidence and water risks
        break;
      case 'placer':
        envRisk += 15; // Water quality impacts
        break;
    }
  }

  // Commodity impact
  if (mineData.primary_commodity) {
    const commodity = mineData.primary_commodity.toLowerCase();

    if (commodity.includes('coal')) {
      envRisk += 25; // High emissions and waste
    } else if (commodity.includes('uranium')) {
      envRisk += 30; // Radioactive materials
    } else if (commodity.includes('copper') || commodity.includes('lead') || commodity.includes('zinc')) {
      envRisk += 15; // Heavy metal contamination risk
    } else if (commodity.includes('gold')) {
      envRisk += 20; // Cyanide and mercury risks
    }
  }

  // Status impact
  if (mineData.status && mineData.status.toLowerCase() === 'abandoned') {
    envRisk += 20; // Unmanaged environmental legacy
  }

  return Math.min(100, Math.max(0, envRisk));
}

/**
 * Calculate social/community risk score
 * @param {Object} mineData - Mine data object
 * @param {Object} countryRisk - Country risk data object
 * @returns {number} Social risk score (0-100)
 */
export function calculateSocialRisk(mineData, countryRisk) {
  // Base social risk from country governance
  let socialRisk = countryRisk ? (countryRisk.score * 0.6) : 30;

  if (!mineData) return Math.min(100, socialRisk);

  // Remote locations may have higher community impact
  if (mineData.latitude && mineData.longitude) {
    // This could be enhanced with actual population density data
    // For now, we'll use a simple heuristic
  }

  // Large-scale operations typically have higher social impact
  if (mineData.production_scale) {
    switch (mineData.production_scale.toLowerCase()) {
      case 'large':
      case 'major':
        socialRisk += 15; // Larger community displacement/impact
        break;
      case 'artisanal':
        socialRisk += 10; // Often informal, less regulated
        break;
    }
  }

  return Math.min(100, Math.max(0, socialRisk));
}

/**
 * Get risk assessment summary for a mine
 * @param {Object} mine - Mine object with risk data
 * @returns {Object} Risk assessment summary
 */
export function getRiskAssessment(mine) {
  if (!mine) return null;

  const overallScore = mine.risk_score || 50;
  const category = getRiskCategory(overallScore);

  return {
    overall: {
      score: overallScore,
      category,
      label: getRiskLabel(category),
      color: getRiskColor(overallScore)
    },
    country: mine.country_risk_score ? {
      score: mine.country_risk_score,
      category: mine.country_risk_category,
      label: getRiskLabel(mine.country_risk_category)
    } : null,
    environmental: mineData => calculateEnvironmentalRisk(mine),
    social: mine.country_risk_score ? calculateSocialRisk(mine, {
      score: mine.country_risk_score
    }) : null,
    factors: getRiskFactors(mine)
  };
}

/**
 * Get list of risk factors for a mine
 * @param {Object} mine - Mine data object
 * @returns {Array} Array of risk factor objects
 */
export function getRiskFactors(mine) {
  const factors = [];

  if (!mine) return factors;

  // Country-level factors
  if (mine.country_risk_score && mine.country_risk_score > 70) {
    factors.push({
      type: 'country',
      level: 'high',
      description: 'High country fragility index',
      impact: 'Political and economic instability'
    });
  }

  // Mine status factors
  if (mine.status) {
    switch (mine.status.toLowerCase()) {
      case 'abandoned':
        factors.push({
          type: 'operational',
          level: 'high',
          description: 'Abandoned mine site',
          impact: 'Environmental legacy issues'
        });
        break;
      case 'suspended':
        factors.push({
          type: 'operational',
          level: 'medium',
          description: 'Operations suspended',
          impact: 'Operational uncertainty'
        });
        break;
    }
  }

  // Commodity factors
  if (mine.primary_commodity) {
    const commodity = mine.primary_commodity.toLowerCase();

    if (commodity.includes('uranium')) {
      factors.push({
        type: 'environmental',
        level: 'high',
        description: 'Radioactive materials',
        impact: 'Radiation and contamination risks'
      });
    } else if (commodity.includes('coal')) {
      factors.push({
        type: 'environmental',
        level: 'medium',
        description: 'Coal mining',
        impact: 'Air quality and climate impact'
      });
    }
  }

  return factors;
}

/**
 * Calculate risk trend (if historical data available)
 * @param {Array} historicalScores - Array of historical risk scores
 * @returns {string} Trend direction ('improving', 'stable', 'deteriorating')
 */
export function calculateRiskTrend(historicalScores) {
  if (!historicalScores || historicalScores.length < 2) {
    return 'stable';
  }

  const recent = historicalScores.slice(-3); // Last 3 data points
  const older = historicalScores.slice(-6, -3); // Previous 3 data points

  if (recent.length < 2 || older.length < 2) {
    return 'stable';
  }

  const recentAvg = recent.reduce((sum, score) => sum + score, 0) / recent.length;
  const olderAvg = older.reduce((sum, score) => sum + score, 0) / older.length;

  const difference = recentAvg - olderAvg;

  if (difference > 5) {
    return 'deteriorating';
  } else if (difference < -5) {
    return 'improving';
  } else {
    return 'stable';
  }
}